import { CdkMenuTrigger } from '@angular/cdk/menu';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { Case } from 'src/app/models/case';
import { Paginator } from 'src/app/models/paginator';
import { CaseService } from 'src/app/services/case.service';
import { MessageService } from 'src/app/services/message.service';

type CaseOption = Case & { selected: boolean, disabled?: boolean, display?: boolean }


@Component({
  selector: 'app-case-select',
  templateUrl: './case-select.component.html',
  styleUrls: ['./case-select.component.scss']
})
export class CaseSelectComponent implements OnInit, OnChanges {
  private id: string;
  @Input() label?: string;
  @Input() labelOutside: boolean = false;
  @Input() search: boolean = false;
  @Input() multiple: boolean = false;

  @Input() initial?: string = 'all';
  @Input() disabled: boolean = false;
  @Input() disabledValues: string[] = [];
  @Input() emitOnInit: boolean = true;

  @Input() hint?: string;
  @Input() error: { value: boolean, message?: string } = {
    value: false,
    message: undefined
  };
  @Input() bottomWrapper: boolean = false;

  loading: boolean = false;
  cases: Paginator<CaseOption> = {
    docs: [],
    page: 1,
    offset: 0,
    limit: 10,
    totalDocs: 0
  };

  selected: CaseOption[] = [];

  query?: string;
  queryUpdate: Subject<string | undefined> = new Subject<string | undefined>();

  @Output() change = new EventEmitter<any>();
  @Input() value?: Case | Case[];
  @Output() valueChange = new EventEmitter<any>();

  show: boolean = false;

  selection?: string;

  subscription: Subscription;

  @ViewChild('trigger') trigger?: CdkMenuTrigger;

  constructor(
    private _message: MessageService,
    private caseService: CaseService,
    private translate: TranslateService
  ) {
    this.id = _.uniqueId("SELECT-");
    this.subscription = this._message.changeEmitted$.subscribe(change => {
      if (change && change.key == "LAYOUT.SELECT") {
        (change.data.id !== this.id) && (this.show = false);
      }
    });
    this.queryUpdate
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(async () => {
        this.resetPage();
        await this.retrieveCases(true);
      });
  }

  ngOnInit(): void {
    this.setInitialState();
  }

  ngOnChanges(changes: SimpleChanges): void {
  }

  _isDisabled(option: Case) {
    return (option._id && this.disabledValues.includes(option._id)) ? true : false;
  }

  _isSelected(option: Case) {
    return (option._id && this.selected.map((t) => t._id).includes(option._id)) ? true : false;
  }

  populate() {
    if (this.multiple && this.value && (this.value as Case[]).length > 0) {
      const values = (this.value as Case[]);
      this.selected = values.map((t) => ({ ...t, selected: true, disabled: false, display: true }));
    } else if (!this.multiple && this.value) {
      const value = (this.value as Case);
      this.selected = [{ ...value, selected: true, disabled: false, display: true }];
    }
  }

  selectedToDisplay() {
    return this.selected.filter((o) => o.display);
  }

  async setInitialState() {
    await this.retrieveCases();
    if (this.multiple && this.value && (this.value as Case[]).length > 0) {
      const values = (this.value as Case[]);
      this.selected = values.map((t) => ({ ...t, selected: true, disabled: false }));
      this.cases.docs.forEach(caseObject => {
        const found = (this.value as Case[]).find(t => t._id === caseObject._id);
        if (found) {
          caseObject.selected = true;
        } else {
          caseObject.selected = false;
        }
      });
      const value = _.unionWith([...this.cases.docs.filter(c => c.selected), ...this.selected], this._caseComparator);

      if (value.length > 0) {
        this.selection = value.length === 1 ? (value[0].identifier || value[0].reference) : `${this.label} (${value.length})`;
      } else {
        this.selection = undefined;
      }
    } else if (!this.multiple && this.value) {
      const value = (this.value as Case);
      this.selected = [{ ...value, selected: true, disabled: false }];
      const option = this.cases.docs.find(t => t._id === value._id);
      if (option) {
        option.selected = true;
      }
      this.selection = value ? (value.identifier || value.reference) : undefined;
    }
    if (this.emitOnInit) {
      this.onChange();
    }
    console.log(this.selected)
  }

  removeFromPreselection(option: any) {
    this.selected.splice(this.selected.findIndex((o) => option._id === o._id), 1);
    const doc = this.cases.docs.find((t) => t._id === option._id);
    if (doc) {
      doc.selected = false;
    }
    this.onChange()
  }

  toggleFromSelection(option: CaseOption) {
    if (!option.selected) {
      this.selected.splice(this.selected.findIndex((o) => option._id === o._id), 1);
    } else if (!this._isSelected(option)) {
      this.selected.push({ ...option, display: false });
    }
  }

  resetPage() {
    this.cases.limit = 10;
    this.cases.page = 1;
    this.cases.offset = 0;
  }

  _caseComparator(caseA: Case, caseB: Case) {
    return caseA._id === caseB._id
  }

  async retrieveCases(replace: boolean = false) {
    this.loading = true;
    const result = await this.caseService.retrieveAll(this.cases.page, this.cases.limit, this.cases.offset, 'reference', [], undefined, this.query);
    this.cases = {
      ...result,
      docs: replace ? [...result.docs.map(t => ({ ...t, disabled: this._isDisabled(t), selected: this._isSelected(t) }))] : _.unionWith([...this.cases.docs, ...result.docs.map(t => ({ ...t, disabled: this._isDisabled(t), selected: this._isSelected(t) }))], this._caseComparator)
    }
    this.loading = false;
  }

  select(option: any) {
    option.selected = option.selected !== null ? !option.selected : true;
    this.toggleFromSelection(option)
    if (!this.multiple) {
      this.cases.docs.forEach(t => {
        if (t._id !== option._id) {
          t.selected = false;
        }
      })
    }
    this.onChange();
  }

  // TODO: handle trademarks having countrycode which is a wipo code (ex: EM)
  onChange() {
    if (!this.multiple) {
      const value = this.cases.docs.find(c => c.selected) !== undefined ? this.cases.docs.find(c => c.selected) : (this.selected.length === 1 ? this.selected[0] : undefined);
      this.selection = value ? (value.identifier || value.reference) : undefined;
      this.valueChange.emit(value);
      this.change.emit({ value });
      this.trigger?.close();
    } else {
      const values = _.unionWith([...this.cases.docs.filter(c => c.selected), ...this.selected], this._caseComparator);
      if (values.length > 0) {
        this.selection = values.length === 1 ? (values[0].identifier || values[0].reference) : `${this.label} (${values.length})`;
      } else {
        this.selection = undefined;
      }
      this.valueChange.emit(values);
      this.change.emit({ value: values });
    }
  }


  toggle() {
    this.show = !this.show;
    if (this.show) {
      this._message.emitChange("LAYOUT", "SELECT", {
        id: this.id
      });
    }
  }

  filtered() {
    return this.cases.docs;
  }


  onClear() {
    if (this.multiple) {
      this.value = [];
    } else {
      this.value = undefined;
    }
    this.cases.docs.forEach(t => {
      t.selected = false;
    });
    this.selected = [];
    this.selection = undefined;
    this.query = undefined;
    this.onChange();
    this.trigger?.close();
  }

  onQueryChange(event: any) {
    this.queryUpdate.next(this.query);
  }


  onScroll() {

    this.cases.offset = this.cases.page * this.cases.limit;
    this.cases.page = this.cases.page + 1;
    this.retrieveCases();
  }
}
